import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils/cn';

/**
 * WorkspaceDialog — centred confirm / form dialog (T-0141).
 *
 * Mirrors `.dialog` in `docs/requirement-library/prototype/selection.css`:
 * a 560px card 12vh from the top over the scrim, with a title and a meta
 * line, a scrolling body, and a footer of right-aligned actions on
 * paper-50. Used for short decisions inside a workspace screen (publish a
 * version, name a new module). Longer multi-step flows use `Sheet`.
 *
 * Built on Radix Dialog like `Sheet` and the command palette, which
 * provides the focus trap, focus return, Esc to close and
 * `role="dialog"`. A scrim click closes it unless `closeOnScrimClick` is
 * false.
 */

export interface WorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  /** Line under the title. */
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Right-aligned footer actions. */
  actions?: React.ReactNode;
  /** Default true. False keeps the dialog open on scrim click. */
  closeOnScrimClick?: boolean;
  /** Width utility override. Default `w-[560px]`. */
  widthClassName?: string;
  className?: string;
}

export function WorkspaceDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  actions,
  closeOnScrimClick = true,
  widthClassName = 'w-[560px]',
  className,
}: WorkspaceDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay data-testid="dialog-scrim" className="fixed inset-0 z-[60] bg-scrim" />
        <Dialog.Content
          {...(description ? {} : { 'aria-describedby': undefined })}
          onInteractOutside={(e) => {
            if (!closeOnScrimClick) e.preventDefault();
          }}
          className={cn(
            'fixed left-1/2 top-[12vh] z-[61] flex max-h-[76vh] max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col overflow-hidden rounded-modal bg-paper-0 shadow-modal focus:outline-none',
            widthClassName,
            className,
          )}
        >
          <div className="flex-none px-5 pb-3 pt-[18px]">
            <Dialog.Title className="text-h2 font-semibold text-ink-900">{title}</Dialog.Title>
            {description ? (
              <Dialog.Description className="mt-0.5 text-meta font-medium text-ink-500">
                {description}
              </Dialog.Description>
            ) : null}
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-5 pb-4">
            {children}
          </div>
          {actions ? (
            <div className="flex flex-none justify-end gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3.5">
              {actions}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

WorkspaceDialog.displayName = 'WorkspaceDialog';

export type WorkspaceNoticeTone = 'neutral' | 'at-risk' | 'critical';

const NOTICE_RULE: Record<WorkspaceNoticeTone, string> = {
  neutral: 'border-l-posture-unassessed',
  'at-risk': 'border-l-posture-at-risk',
  critical: 'border-l-posture-critical',
};

/**
 * Left-rule notice (`.alert` in the prototype tokens) for dialogs and
 * panels. `critical` is announced as an alert; the others are status.
 */
export function WorkspaceNotice({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: WorkspaceNoticeTone;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'critical' ? 'alert' : 'status'}
      className={cn(
        'rounded-card border border-l-4 border-ink-100 bg-paper-0 px-4 py-3 text-body-sm text-ink-700',
        NOTICE_RULE[tone],
        className,
      )}
    >
      {children}
    </div>
  );
}
